import React, { useEffect, useState } from "react"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/20/solid"

/**
 * Reusable dropdown menu component based on Headless UI
 * @param {Object} props - Component props
 * @param {string} props.buttonText - Text to display on the button
 * @param {Array} props.options - Array of option objects with id and name properties
 * @param {string} props.selectedOption - Currently selected option id
 * @param {Function} props.onSelect - Function called when an option is selected
 * @param {string} [props.buttonClassName] - Optional custom class for the button
 * @param {string} [props.menuClassName] - Optional custom class for the menu
 * @param {string} [props.itemClassName] - Optional custom class for menu items
 * @returns {JSX.Element} - Rendered dropdown menu
 */
export default function DropdownMenu({
	buttonText,
	options = [],
	selectedOption,
	onSelect,
	buttonClassName = "",
	menuClassName = "",
	itemClassName = ""
}) {
	// Local state to track the actual selected option
	const [internalSelectedOption, setInternalSelectedOption] = useState(selectedOption)

	// Update internal state when selectedOption prop changes
	useEffect(() => {
		setInternalSelectedOption(selectedOption)
	}, [selectedOption])

	const defaultButtonClass =
		"inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
	const defaultMenuClass =
		"absolute left-0 z-10 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
	const defaultItemClass =
		"block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"

	const handleSelect = optionId => {
		// Update internal state first
		setInternalSelectedOption(optionId)
		// Call parent's handler
		if (onSelect) {
			onSelect(optionId)
		}
	}

	return (
		<Menu as="div" className="relative inline-block text-left">
			{({ open, close }) => (
				<>
					<div>
						<MenuButton className={buttonClassName || defaultButtonClass}>
							{buttonText}
							<ChevronDownIcon
								aria-hidden="true"
								className="-mr-1 size-5 text-gray-400"
							/>
						</MenuButton>
					</div>

					{open && (
						<MenuItems static className={menuClassName || defaultMenuClass}>
							<div className="py-1">
								{options.map(option => (
									<MenuItem key={option.id}>
										{({ active }) => (
											<button
												onClick={() => {
													handleSelect(option.id)
													close()
												}}
												className={`w-full text-left ${itemClassName || defaultItemClass} ${
													internalSelectedOption === option.id
														? "bg-blue-100 text-blue-900 font-medium"
														: active
															? "bg-gray-100"
															: ""
												}`}
											>
												{option.name}
											</button>
										)}
									</MenuItem>
								))}
							</div>
						</MenuItems>
					)}
				</>
			)}
		</Menu>
	)
}
